import type { QuizTeamMember } from './quiz-player';
import type { Player } from '@api-service/players';
import type {
  TeamEntryMethod,
  TeamRatingFailureKind
} from '@lib/api/team-rating';
import type { ScreenshotMatch } from '@lib/quiz/screenshot-squad';

export type GytrTeamMember = QuizTeamMember;

export type GytrTeamSource = 'screenshot' | 'fplId' | 'manual' | null;

export type Gw1PerformanceData = {
  captainActualPoints: number;
  optimalCaptainPoints: number;
  optimalCaptainName: string;
  missingHighOwnershipPlayer: string;
  missingPlayerOwnership: number;
  missingPlayerGw1Points: number;
  squadGameweekPoints: number;
  percentile: number | null;
};

export type ScreenshotPlayer = {
  id: string;
  displayName: string;
};

/**
 * What a screenshot import ended in.
 *
 * `needs-review` is a read the parser managed but the rules refuse: a squad
 * that is not 2 GK / 5 DEF / 5 MID / 3 FWD, a player read twice, or fewer than
 * fifteen found. It is not a failure: the read is good enough to put on a
 * pitch, so it routes to the review step rather than showing an error
 * (FFH-2738).
 */
export type ScreenshotImportOutcome = 'imported' | 'needs-review' | 'error';

/** A rating that failed, as the funnel needs to read it. */
export type GytrCalculationFailure = {
  /** What the failure said. Shown as a diagnostic line, never as a headline. */
  message: string;
  kind: TeamRatingFailureKind;
  status: number | null;
  /**
   * A truncated Datadog RUM session id the visitor can read out to support.
   * Null wherever RUM is not recording, in which case nothing is shown rather
   * than a code that matches no session.
   */
  reference: string | null;
};

// One transfer surfaced on the improvement step. Only the OUT player and the
// points uplift are exposed — the IN recommendation is the product and stays
// locked (spec 5.5).
export type GytrTopTransfer = {
  outPlayerId: string;
  pointsUplift: number;
};

export type GytrState = {
  team: GytrTeamMember[];
  teamSource: GytrTeamSource;
  /**
   * How the squad currently held in state was assembled, sent with the rating
   * request so invalid squads can be attributed to the picker or to screenshot
   * import. Null for an FPL-sourced squad, which the backend fetches itself.
   */
  entryMethod: TeamEntryMethod | null;
  fplId: string;
  playerUuids: ScreenshotPlayer[] | null;
  /**
   * The last screenshot read, kept whole rather than reduced to ids.
   *
   * The import endpoint sends each player's position, price and a confidence
   * score, which is everything the review pitch needs to place the squad and to
   * decide which surplus read to give up on. Retained after the review is
   * confirmed so a rating the API still refuses can reopen it.
   */
  screenshotMatches: ScreenshotMatch[] | null;
  /** Whether the review pitch is the step being shown. */
  isReviewingScreenshot: boolean;
  optimumPoints: number | null;
  teamRating: number | null;
  potentialRating: number | null;
  /**
   * The transfers to show on the improvement step, best first, up to three
   * (FFH-2382). Empty where the solve named none, or while one is pending.
   */
  topTransfers: GytrTopTransfer[];
  /** The best of `topTransfers`, or null where there are none. */
  topTransfer: GytrTopTransfer | null;
  /**
   * Whether the best transfer is still being solved for.
   *
   * Separate from `isCalculating`, which covers the rating alone. The rating is
   * final the moment it arrives and the funnel advances on it, so folding the
   * solve into that flag would hold the visitor on the processing step for the
   * whole wait — the very delay deferring the solve was meant to remove.
   *
   * True only between a SUGGESTION_PENDING rating and the suggestion landing,
   * being abandoned, or the wait running out. Never true when the async flag is
   * off, because the outcome cannot occur.
   */
  isSuggestionPending: boolean;
  /** The AI's sell (lowest-projected starter) from the team-rating derived
   * block; the transfer step's OUT fallback when the solver makes no move. */
  aiSellPlayerId: string | null;
  /** Whether the squad's lowest-projected starter is genuinely weak (below the
   * AI's threshold), from the team-rating response. Gates the reveal insight. */
  hasWeakStarter: boolean;
  /** Full Player objects (portraits) for the rated squad, keyed by UUID. */
  playerPortraits: Map<string, Player> | null;
  gw1Performance: Gw1PerformanceData | null;
  /**
   * The address the email gate captured.
   *
   * Held here because the provider is what reports the completion, and the
   * snapshot PATCH sends this as the fallback identity for a completion whose
   * `_funnel_session` cookie did not travel (see ffh-public-api FFH-2376).
   * Also the paywall's Auth0 login hint.
   */
  capturedEmail: string | null;
  isImporting: boolean;
  isCalculating: boolean;
  importError: string | null;
  /**
   * Why the last rating did not come back, or null when nothing has failed.
   *
   * Held whole rather than as a message, because three things read it and none
   * of them should be parsing prose: the funnel routes a refused squad to the
   * review pitch (FFH-2738), the failure state picks its explanation from the
   * kind, and the error reporting groups on it (FFH-2743).
   */
  calculationFailure: GytrCalculationFailure | null;
};

export type GytrActions = {
  importTeamFromScreenshot: (
    file: File,
    readErrorText?: string
  ) => Promise<ScreenshotImportOutcome>;
  importTeamByFplId: (fplId: string) => Promise<void>;
  setTeamManually: (team: GytrTeamMember[]) => void;
  /**
   * The squad the visitor confirmed on the review pitch. Separate from
   * `setTeamManually` because the screenshot chose these players, so the entry
   * method stays SCREENSHOT through the corrections.
   */
  setTeamFromReview: (team: GytrTeamMember[]) => void;
  setIsReviewingScreenshot: (isReviewing: boolean) => void;
  /** `attempt` counts from 1 and is reported, so repeat failures are visible. */
  calculateRating: (attempt?: number) => Promise<void>;
  setCapturedEmail: (email: string) => void;
  resetImportError: () => void;
  resetForRetry: () => void;
};

export type GytrContextValue = {
  state: GytrState;
  actions: GytrActions;
  nextGameweek: number;
};

export const INITIAL_GYTR_STATE: GytrState = {
  team: [],
  teamSource: null,
  entryMethod: null,
  fplId: '',
  playerUuids: null,
  screenshotMatches: null,
  isReviewingScreenshot: false,
  optimumPoints: null,
  teamRating: null,
  potentialRating: null,
  topTransfers: [],
  topTransfer: null,
  isSuggestionPending: false,
  aiSellPlayerId: null,
  hasWeakStarter: false,
  playerPortraits: null,
  gw1Performance: null,
  capturedEmail: null,
  isImporting: false,
  isCalculating: false,
  importError: null,
  calculationFailure: null
};
